import { useCallback, useMemo, useRef, useState } from "react";
import {
	CellValueChangedEvent,
	ColDef,
	ColumnState,
	RowClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { aggridHeaderClass, commonCodeCellStyle } from "src/components/common/common";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { formatDate } from "src/utils/string/stringUtil";
import {
	DateRow,
	defaultDateRow,
	useTQC2080_R01Store,
} from "./Store_TQC2080_R01";
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';
import { cellDateComparator } from 'src/utils/grid/gridFunctionUtil';

const COL_STATE_KEY = "TQC2080_R01-datelist-column-state";
const DAY_OF_WEEK_NAMES = ["일", "월", "화", "수", "목", "금", "토"] as const;

interface Props {
	/** 저장·신규 행 공통: 클릭 시 우측 상세(마스터 템플릿) 로드 */
	onDateRowClick: (row: DateRow) => void;
}

export const DateList_TQC2080_R01 = ({ onDateRowClick }: Props) => {
	const {
		dateRowList,
		updateDateList,
		deletedDateList, //  
		TQC2080_R01Filter,
		scrollTop,
		setScrollTop,
		setDetailScrollTop,
	} = useTQC2080_R01Store();

	const gridRef = useRef<AgGridReact<DateRow>>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const [columnState, setColumnState] = useState<ColumnState[] | null>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	// ── useGridSettingContent ────────────────────────────────────────────
	const {
		getRowId,
		createRowClassRules,
	} = useGridSettingContent<DateRow>({
			gridRef,
			key: "valueIndex",
			gridData: dateRowList,
			defaultGridData: defaultDateRow,
			selectedRow: undefined,
			originalGridData: dateRowList,
			updateRow: (index, data) => updateDateList(Number(index), data),
		});

	// ── 선택 날짜 → valueIndex 변환 (행 하이라이팅용) ───────────────────
	const selectedValueIndex = useMemo(
		() =>
			dateRowList.find(
				(r) => r.analysisDt === TQC2080_R01Filter.searchAnalysisDt,
			)?.valueIndex,
		[dateRowList, TQC2080_R01Filter.searchAnalysisDt],
	);

	//  : 삭제 예정 날짜 → valueIndex 변환 (빨간행)
	const deletedValueIndexes = useMemo(
		() =>
			dateRowList
				.filter((r) => deletedDateList.includes(r.analysisDt))
				.map((r) => r.valueIndex),
		[dateRowList, deletedDateList],
	);

	// ── 컬럼 정의 ────────────────────────────────────────────────────────
	const columnDefs: ColDef<DateRow>[] = useMemo(
		() => [
			...getDefaultRowIndexColumnConfig(),
			// 날짜: 달력은 일 자 더블클릭만(신규·저장 공통). 단일 클릭은 행 조회용.
			{
				headerName: "일 자",
				colId: "analysisDt",
				field: "analysisDt",
				flex: 1,
				headerClass: aggridHeaderClass,
				cellStyle: (params) => ({
					...commonCodeCellStyle,
					...onlyTouchGridStyle("analysisDt", "analysisDt")(params),
				}),
				editable: (params) => params.data?.inserted === true,
				suppressClickEdit: true,
				comparator: cellDateComparator,
				valueFormatter: (params) =>
					params.data?.analysisDt
						? (() => {
								const date = new Date(params.data.analysisDt);
								const formattedDate = formatDate(date);
								if (Number.isNaN(date.getTime())) return formattedDate;
								return `${formattedDate} (${DAY_OF_WEEK_NAMES[date.getDay()]})`;
							})()
						: "",
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dateRowList, cellDateComparator, hoveredRowIndex, selectedValueIndex, onlyTouchGridStyle],
	);

	//  변경: deletedList에 deletedValueIndexes  → 빨간행 표시
	const rowClassRules = useMemo(
		() =>
			createRowClassRules<DateRow>({
				idKey: "valueIndex",
				selectedList: selectedValueIndex != null ? [selectedValueIndex] : [],
				deletedList: deletedValueIndexes,
			}),
		[createRowClassRules, selectedValueIndex, deletedValueIndexes],
	);

	// ── 셀 값 변경 : inserted 행에서 analysisDt 직접 입력 시 store 반영 ─
	const handleCellValueChanged = useCallback(
		(e: CellValueChangedEvent<DateRow>) => {
			if (e.node.rowPinned === "bottom" || !e.data) return;
			updateDateList(e.data.valueIndex, {
				...e.data,
				[e.colDef.field!]: e.newValue,
			});
		},
		[updateDateList],
	);

	//  변경: 삭제 예정(빨간) 행은 클릭해도 우측 그리드 로드 차단
	const handleRowClick = useCallback(
		(e: RowClickedEvent<DateRow>) => {
			if (!e.data) return;
			if (e.data.analysisDt && deletedDateList.includes(e.data.analysisDt)) return;
			setDetailScrollTop(null);
			onDateRowClick(e.data);
		},
		[onDateRowClick, deletedDateList, setDetailScrollTop],
	);

	return (
		<GridContextWrapper<DateRow>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={dateRowList}
			setPaddedGridData={() => {}}
			initSelected={() => {}}
			defaultRowData={{
				analysisDt: "",
				inserted: true,
				isModified: false,
				valueIndex: 0,
				orderIndex: 0,
				orderIndexSeq: 0,
			}}
			allSelected={() => {}}
			baseColumnDefs={columnDefs}
			setColumnState={setColumnState}
			localStorageKey={COL_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Select<DateRow>
				ref={gridRef}
				rowData={dateRowList}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId} // +- 버튼 테이블은 valueIndex로 고정
				rowClassRules={rowClassRules}
				onRowClicked={handleRowClick}
				onCellValueChanged={handleCellValueChanged}
				columnStateKey={COL_STATE_KEY}
				setColumnState={setColumnState}
				setHoveredRowIndex={setHoveredRowIndex}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
